import { useEffect } from 'react'
import {
  consentManager,
  fromLizeeConsent,
  FULL_CONSENT,
  lizeeConsent,
  NO_CONSENT,
  readCookiebotConsent,
  ConsentState,
} from './consent'

/**
 * Calls `onConsent` with the visitor's choice once they have made one, then on
 * every change. Nothing is called before a choice: no tag may load until then.
 */
const useConsent = (onConsent: (consent: ConsentState) => void) => {
  useEffect(() => {
    const manager = consentManager()

    if (manager === 'none') {
      onConsent(FULL_CONSENT)
      return
    }

    if (manager === 'lizee') {
      // consent.js is a blocking script in <head>: it is already here.
      const lizee = lizeeConsent()
      if (!lizee) return
      let active = true
      lizee.ready.then(() => {
        if (active && lizee.hasChoice)
          onConsent(fromLizeeConsent(lizee.consent))
      })
      const off = lizee.onChange((consent) =>
        onConsent(fromLizeeConsent(consent))
      )
      return () => {
        active = false
        off()
      }
    }

    const script = document.createElement('script')
    script.src = `https://consent.cookiebot.com/uc.js?cbid=${process.env.COOKIEBOT_DOMAIN_ID}`
    script.async = true
    document.head.appendChild(script)

    const onAccept = () => onConsent(readCookiebotConsent())
    const onDecline = () => onConsent(NO_CONSENT)
    window.addEventListener('CookiebotOnAccept', onAccept)
    window.addEventListener('CookiebotOnDecline', onDecline)

    return () => {
      window.removeEventListener('CookiebotOnAccept', onAccept)
      window.removeEventListener('CookiebotOnDecline', onDecline)
      document.head.removeChild(script)
    }
  }, [onConsent])
}

export default useConsent
